import { useState } from 'react'
import { Store, User, KeyRound, Eye, EyeOff, Loader2, Wifi, ArrowRight, Database, ShieldCheck, Zap, HelpCircle, AlertCircle } from 'lucide-react'
import toast from 'react-hot-toast'
import { login, ApiError } from '../api'
import { setUser, setMeta } from '../db'
import HelpSheet from '../components/HelpSheet'

/**
 * Login screen. Three fields — pharmacy name, username, password.
 *
 * Why pharmacy name (rather than a code or subdomain): per the design doc
 * §11.1, this matches the existing stock-take portal UX so staff who already
 * use that surface have one less thing to learn.
 *
 * Persistence: on success we write the session token + an expiry into Dexie;
 * App.jsx restores it on the next launch. If the cashier loses the device,
 * the desktop admin can deactivate the user → next login refresh fails → the
 * cached token is wiped on the next API call.
 */
export default function LoginScreen({ onLoggedIn }) {
  const [pharmacyName, setPharmacyName] = useState('')
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [showPw, setShowPw] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [helpOpen, setHelpOpen] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!pharmacyName.trim() || !username.trim() || !password) {
      setError('Enter your pharmacy name, username and password.')
      return
    }
    setError('')
    setBusy(true)
    try {
      const res = await login({
        pharmacyName: pharmacyName.trim(),
        username: username.trim(),
        password,
      })

      // Token TTL on the server is 12h (dispenserAuth.js). We persist the
      // expiry so a session that's already dead can be detected without a
      // network round-trip.
      const tokenExp = Date.now() + 11 * 60 * 60 * 1000
      await setUser({
        ...res.user,
        token: res.token,
        token_exp: tokenExp,
      })
      await setMeta('pharmacy_name', res.pharmacy_name || pharmacyName.trim())
      if (res.snapshot_at) await setMeta('snapshot_at', res.snapshot_at)

      toast.success(`Welcome, ${res.user?.full_name || res.user?.username}`)
      onLoggedIn({ user: { ...res.user, token: res.token }, pharmacy_name: res.pharmacy_name, snapshot_at: res.snapshot_at })
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === 'LICENSE_TIER_NOT_ELIGIBLE') {
          setError('Offline Mode is not enabled for this pharmacy. It needs a Monthly or Annual license. Ask your administrator.')
        } else if (err.code === 'NETWORK') {
          setError('No internet connection. Sign in once while online to prepare this device.')
        } else {
          // The server sends specific, actionable messages (wrong pharmacy
          // name, unknown user, unsynced password, …) — show them verbatim.
          setError(err.message)
        }
      } else {
        setError('Sign-in failed. Please try again.')
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <main className="pc-login-shell text-sage-900">
      <div className="pc-login-frame pc-rise">
        <section className="pc-login-context" aria-label="About the offline register">
          <div className="pc-login-brand">
            <BrandMark ringClass="ring-brand-800" />
            <div>
              <p className="text-lg leading-none font-semibold text-white">PharmaCare</p>
              <p className="mt-1.5 text-sm text-[#a8bbb2]">Offline register</p>
            </div>
          </div>

          <div className="pc-login-message">
            <span>For power cuts and network outages</span>
            <h2>Keep selling during an outage.</h2>
            <p>Sell from the stock saved on this device, record every payment, and upload automatically when the connection returns.</p>
          </div>

          <div className="pc-login-assurances mt-auto">
            <Assurance icon={Database} title="Stock saved on this device" text="Your catalogue is stored on this phone or laptop." />
            <Assurance icon={Zap} title="Cash, MoMo and card" text="Take payments while the main computer is down." />
            <Assurance icon={ShieldCheck} title="Sales upload later" text="Sales wait on the device until PharmaCare receives them." />
          </div>
        </section>

        <section className="pc-login-card">
          <div className="pc-login-card-brand">
            <BrandMark ringClass="ring-white" />
            <div>
              <p className="font-display text-xl font-semibold text-brand-800 leading-none">PharmaCare</p>
              <p className="mt-1 text-sm text-sage-600">Offline register</p>
            </div>
          </div>

          <div className="mb-6">
            <p className="text-xs text-brand-600 uppercase tracking-[0.12em] font-semibold">Staff sign-in</p>
            <h1 className="font-display text-[26px] leading-tight font-semibold text-sage-900 mt-1.5">Open the register</h1>
            <p className="text-[15px] leading-relaxed text-sage-600 mt-2">Use the same pharmacy name, username and password as the PharmaCare desktop app.</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            <Field id="pharmacy" icon={Store} label="Pharmacy name" value={pharmacyName} onChange={setPharmacyName} autoCapitalize="words" autoComplete="organization" />
            <Field id="username" icon={User} label="Username" value={username} onChange={setUsername} autoCapitalize="none" autoComplete="username" />
            <Field
              id="password"
              icon={KeyRound}
              label="Password"
              type={showPw ? 'text' : 'password'}
              value={password}
              onChange={setPassword}
              autoCapitalize="none"
              autoComplete="current-password"
              rightSlot={
                <button
                  type="button"
                  onClick={() => setShowPw(s => !s)}
                  className="h-11 w-11 mr-0.5 grid place-items-center rounded-lg text-sage-500 hover:bg-sage-100 active:text-sage-800"
                  aria-label={showPw ? 'Hide password' : 'Show password'}
                  aria-pressed={showPw}
                >
                  {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              }
            />

            {error && (
              <p role="alert" className="flex items-start gap-2.5 rounded-xl bg-rose-50 ring-1 ring-rose-200 px-3.5 py-3 text-sm font-medium leading-relaxed text-rose-800">
                <AlertCircle size={17} className="shrink-0 mt-0.5" /> <span>{error}</span>
              </p>
            )}

            <button
              type="submit"
              disabled={busy}
              className="w-full !mt-5 h-12 rounded-xl bg-brand-600 text-white text-[15px] font-semibold shadow-lg shadow-brand-800/20 hover:bg-brand-700 active:bg-brand-700 disabled:opacity-60 flex items-center justify-center gap-2 transition-colors"
            >
              {busy ? <><Loader2 size={17} className="animate-spin" /> Checking access…</> : <>Open register <ArrowRight size={17} /></>}
            </button>
          </form>

          <div className="mt-5 flex items-start gap-3 rounded-xl bg-sage-50 ring-1 ring-sage-200 px-4 py-3.5">
            <Wifi size={17} className="text-brand-600 shrink-0 mt-0.5" />
            <p className="text-sm leading-relaxed text-sage-700">
              <b className="font-semibold text-sage-900">Sign in while you are online.</b> That downloads your stock to this device; after that it keeps selling without internet for the rest of the shift (about 11 hours).
            </p>
          </div>

          <button
            type="button"
            onClick={() => setHelpOpen(true)}
            className="mt-4 self-start inline-flex items-center gap-2 min-h-11 px-1 text-sm font-semibold text-brand-600 hover:text-brand-700"
          >
            <HelpCircle size={17} /> Help and install instructions
          </button>
        </section>
      </div>
      <HelpSheet open={helpOpen} onClose={() => setHelpOpen(false)} />
    </main>
  )
}

function BrandMark({ ringClass }) {
  return (
    <div className={`w-12 h-12 rounded-[14px] overflow-hidden shadow-lg shadow-black/20 ring-2 ${ringClass} bg-white shrink-0`}>
      <img src={`${import.meta.env.BASE_URL}icon-192.png`} alt="" className="w-full h-full object-cover" />
    </div>
  )
}

function Assurance({ icon: Icon, title, text }) {
  return (
    <div className="pc-login-assurance">
      <span><Icon size={18} /></span>
      <div><b>{title}</b><p>{text}</p></div>
    </div>
  )
}

function Field({ id, icon: Icon, label, value, onChange, type = 'text', autoCapitalize = 'sentences', autoComplete, rightSlot }) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-semibold text-sage-900 mb-1.5">
        {label}
      </label>
      <div className="flex items-center bg-white ring-1 ring-line-strong rounded-xl focus-within:ring-2 focus-within:ring-brand-600 transition-shadow">
        <Icon size={18} className="text-sage-400 ml-3.5 shrink-0" aria-hidden="true" />
        <input
          id={id}
          type={type}
          value={value}
          onChange={e => onChange(e.target.value)}
          autoCapitalize={autoCapitalize}
          autoComplete={autoComplete}
          autoCorrect="off"
          spellCheck="false"
          className="flex-1 min-w-0 h-12 px-3 bg-transparent text-base font-medium text-sage-900 focus:outline-none"
        />
        {rightSlot}
      </div>
    </div>
  )
}
